import React, {useContext} from 'react';
import type {CurrencyCode} from '@wix/devzai-utils-common';
import {arrayRemoveUndefinedValues, assertNotNullable, evaluateFunction, iterableFindAndSelect} from '@wix/devzai-utils-common';
import type {
    DoppeBusinessEntityAutoListToSingleEntityMap,
    DoppeBusinessEntityData,
    DoppeBusinessEntityReference,
    DoppeDtoBymoPageOwnerData,
    DoppeDtoSpeedDialLinkViewerData,
    DoppeHtmlSiteInfo,
    DoppePremiumFeaturesStatus
} from '../../client-server-common';
import {
    DoppeAppVariant,
    DoppeBusinessEntity,
    doppeBusinessEntityAutoListResolveSingleEntityType,
    doppeBusinessEntityDtoIsAvailable,
    doppeBusinessEntityDtoIsNotFound,
    DoppeBymoPageFreemiumBannerType,
    DoppeDtoBymoPageType,
    doppeDtoUserGetAppVariant,
    doppeDtoUserGetLinkedMetaSiteId
} from '../../client-server-common';
import {DoppeLinkSpecEntityType} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeDtoStoreProduct} from '../../client-server-common/types/doppe-dto-store-product';
import type {DoppeDtoBookingService} from '../../client-server-common/types/doppe-dto-booking-service';
import type {DoppeDtoBlogPost} from '../../client-server-common/types/doppe-dto-blog-post';
import type {DoppeDtoEvent} from '../../client-server-common/types/doppe-dto-event';
import type {
    WixBlogApiPostInfo,
    WixBookingApiServiceInfo,
    WixCalendarEventSession,
    WixEventApiEventInfo,
    WixEventTicketDefinition,
    WixImageResource,
    WixStoresApiProductInfo
} from '@wix/devzai-common-wix';
import type {DoppeDtoViewerFeatures} from '../../client-server-common/types/doppe-dto-viewer-features';
import {DoppeDtoViewerFeaturesDefaultValues} from '../../client-server-common/types/doppe-dto-viewer-features';
import type {
    DoppeCookieBannerSettings,
    DoppeGoogleAnalyticsSettings
} from '../../client-server-common/types/doppe-dto-user-settings';
import type {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {DoppeDtoMembershipPlan} from '../../client-server-common/types/doppe-dto-membership-plan';

export interface BymoPageSpeedDialData {
    links: DoppeDtoSpeedDialLinkViewerData[];
    charsCount: number;
}

/**
 * Dummy items provided by the editor for previewing vertical item pages.
 * When the viewer is in editor preview mode with no item slug,
 * it will use these dummy items to render a preview.
 */
export interface BymoPageEditorDummyItems {
    /** Dummy product for product item page preview */
    dummyProduct?: WixStoresApiProductInfo;
    /** Dummy digital product for digital product item page preview */
    dummyDigitalProduct?: WixStoresApiProductInfo;
    /** Dummy service for service item page preview */
    dummyService?: WixBookingApiServiceInfo;
    /** Dummy course for course item page preview */
    dummyCourse?: WixBookingApiServiceInfo;
    /** Dummy appointment for appointment item page preview */
    dummyAppointment?: WixBookingApiServiceInfo;
    /** Dummy class for class item page preview */
    dummyClass?: WixBookingApiServiceInfo;
    /** Dummy blog post for blog post item page preview */
    dummyBlogPost?: WixBlogApiPostInfo;
    /** Dummy event for RSVP event item page preview */
    dummyEvent?: WixEventApiEventInfo;
    /** Dummy event for ticketing event item page preview */
    dummyTicketingEvent?: WixEventApiEventInfo;
    /** Dummy ticket definitions for ticketing event page preview */
    dummyTicketDefinitions?: WixEventTicketDefinition[];
    /** Dummy course sessions for course item page schedule preview */
    dummyCourseSessions?: WixCalendarEventSession[];
    /** Dummy class sessions for class item page available sessions preview */
    dummyClassSessions?: WixCalendarEventSession[];
}

export interface BymoPageEditorContextProps {
    editorSelectedActionsIds?: DoppeDtoAction.Id[];
    editorEditedActionId?: DoppeDtoAction.Id | null;
    /**
     * Dummy items for previewing vertical item pages in editor mode.
     * When no item slug is provided, the viewer will use these dummy items.
     * If not provided and no slug, a "Not Found" error will be shown.
     */
    editorDummyItems?: BymoPageEditorDummyItems;
    /** When true, shows the page lock guard in the editor live preview. Default is false (page unlocked for editing). */
    editorShowPageLockPreview?: boolean;
}

export interface BymoPageContextProps extends Required<DoppeDtoViewerFeatures>, BymoPageEditorContextProps {
    targetUrl: string | null;
    targetUrlImage: WixImageResource | null;
    userDisplayName: string;
    userProfilePicture: WixImageResource | null;
    userAccountName: string;
    userPrivacyPolicyUrl?: string;
    userTermsOfServiceUrl?: string;
    userCookieBannerSettings?: DoppeCookieBannerSettings;
    userGoogleAnalyticsSettings?: DoppeGoogleAnalyticsSettings;
    speedDialData?: BymoPageSpeedDialData;
    linkedMetaSiteId: string | null;
    businessEntitiesData?: DoppeBusinessEntityData[];
    premiumFeaturesStatus: DoppePremiumFeaturesStatus | null;
    htmlSiteInfo: DoppeHtmlSiteInfo | null;
    appVariant: DoppeAppVariant;
    /**
     * Whether this is a Link in Bio (SpeedDial) page.
     * Used to conditionally render elements that should only appear on Instant Pages.
     */
    isSpeedDialPage?: boolean;
    /**
     * The site's default payment currency from Wix site properties.
     * Used for WixEcom checkout to display prices in the correct currency.
     */
    sitePaymentCurrency?: CurrencyCode | null;
    /** The OAuth app client ID for Wix Headless SDK integration. */
    headlessOAuthAppClientId?: string;
}

export function bymoPageContextPropsResolveFromPageOwnerData (
    pageOwnerData: DoppeDtoBymoPageOwnerData,
    pageType: DoppeDtoBymoPageType
) : BymoPageContextProps {
    return bymoPageContextPropsExtendDefaultValues({
        userDisplayName: pageOwnerData.displayName,
        userTermsOfServiceUrl: pageOwnerData.userSettings.termsOfServiceUrl,
        userPrivacyPolicyUrl: pageOwnerData.userSettings.privacyPolicyUrl,
        userCookieBannerSettings: pageOwnerData.userSettings.cookieBannerSettings,
        userProfilePicture: pageOwnerData.profilePicture,
        userAccountName: pageOwnerData.subdomain,
        viewerHideFooterBanner: pageOwnerData.userSettings.viewerHideFooterBanner,
        viewerHideWatermark: pageOwnerData.userSettings.viewerHideWatermark,
        linkedMetaSiteId: doppeDtoUserGetLinkedMetaSiteId(pageOwnerData),
        appVariant: doppeDtoUserGetAppVariant(pageOwnerData),
        freemiumBannerType: pageOwnerData.userSettings.freemiumBannerType ?? DoppeBymoPageFreemiumBannerType.HoppDefault,
        // Vertical item page flags
        disableHoppVerticalProductPages: pageOwnerData.userSettings.disableHoppVerticalProductPages,
        useHoppVerticalServicePages: pageOwnerData.userSettings.useHoppVerticalServicePages,
        useHoppVerticalBlogPostPages: pageOwnerData.userSettings.useHoppVerticalBlogPostPages,
        useHoppVerticalEventPages: pageOwnerData.userSettings.useHoppVerticalEventPages,
        useProductOldSkin: pageOwnerData.userSettings.useProductOldSkin,
        headlessOAuthAppClientId: pageOwnerData.userSettings.creatorsMetaSiteHeadlessOAuthAppId,
    }, pageType === DoppeDtoBymoPageType.PreRoll)
}

export function bymoPageContextPropsAdjustByPageType (
    bymoPageContextProps: BymoPageContextProps,
    pageType: DoppeDtoBymoPageType
) : BymoPageContextProps {
    return {
        ...bymoPageContextProps,
        targetUrl: pageType === DoppeDtoBymoPageType.PreRoll ? 'https://www.example.com' : null,
    }
}

export function bymoPageContextPropsExtendDefaultValues (
    partialContextProps: Partial<BymoPageContextProps> = {},
    withTargetUrl = true
) : BymoPageContextProps {
    return {
        userAccountName: 'user',
        userProfilePicture: null,
        userDisplayName: 'John Doe',
        targetUrlImage: null,
        linkedMetaSiteId: null,
        targetUrl: withTargetUrl ? 'https://hopp.co' : null,
        premiumFeaturesStatus: null,
        htmlSiteInfo: null,
        appVariant: DoppeAppVariant.Default,
        ...DoppeDtoViewerFeaturesDefaultValues,
        ...partialContextProps
    }
}

export const BymoPageContextPropsContext = React.createContext<BymoPageContextProps | null>(null);

export function useBymoPageContextProps () {
    return assertNotNullable(useContext((BymoPageContextPropsContext)));
}

/**
 * Returns the site's default payment currency from the viewer context.
 * Used for WixEcom checkout to display prices in the correct currency.
 */
export function useDoppeViewerSitePaymentCurrency(): CurrencyCode | null {
    const contextProps = useBymoPageContextProps();
    return contextProps.sitePaymentCurrency ?? null;
}

export function bymoPageContextPropsResolveBusinessEntity<E extends DoppeBusinessEntity> (
    bymoPageContextProps: BymoPageContextProps,
    businessEntityReference: DoppeBusinessEntityReference<E>
) {
    const entityType = businessEntityReference.businessEntity;
    const entityId = businessEntityReference.id;

    return iterableFindAndSelect(bymoPageContextProps.businessEntitiesData ?? [], (businessEntity, skip) => {
        return businessEntity.type === entityType && businessEntity.id === entityId ? businessEntity.dto : skip;
    }) as DoppeBusinessEntityData.ResolveDtoFromType<E> | undefined
}

/**
 * Resolves the individual item DTOs from a list entity.
 * List entities store IDs (string[]), and this function looks up each ID
 * as a single entity in businessEntitiesData and returns the resolved DTOs.
 */
export function bymoPageContextPropsResolveBusinessEntityListItems<E extends keyof DoppeBusinessEntityAutoListToSingleEntityMap>(
    bymoPageContextProps: BymoPageContextProps,
    businessEntityReference: DoppeBusinessEntityReference<E>
): DoppeBusinessEntityData.ResolveListItemDtoFromType<E>[] {
    const ids = bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, businessEntityReference);

    if (!ids) {
        return [];
    }

    const singleEntityType = doppeBusinessEntityAutoListResolveSingleEntityType(businessEntityReference.businessEntity);

    return arrayRemoveUndefinedValues(ids.map(id => {
        const dto = bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
            businessEntity: singleEntityType,
            id: id
        });

        return doppeBusinessEntityDtoIsAvailable(dto) ? dto as DoppeBusinessEntityData.ResolveListItemDtoFromType<E> : undefined;
    }));
}

/**
 * Resolves the slug for an entity from the business entities data.
 * Returns empty string if the entity is not found or doesn't have a slug.
 */
export function bymoPageContextPropsResolveEntitySlug(
    bymoPageContextProps: BymoPageContextProps,
    entityType: DoppeLinkSpecEntityType,
    entityId: string
): string {

    const businessEntityType = evaluateFunction(() => {
        switch (entityType) {
            case DoppeLinkSpecEntityType.Product:
                return DoppeBusinessEntity.HoppProduct;
            case DoppeLinkSpecEntityType.Service:
                return DoppeBusinessEntity.HoppService;
            case DoppeLinkSpecEntityType.BlogPost:
                return DoppeBusinessEntity.HoppBlogPost;
            case DoppeLinkSpecEntityType.Event:
                return DoppeBusinessEntity.HoppEvent;
            case DoppeLinkSpecEntityType.PricingPlan:
                return DoppeBusinessEntity.HoppPricingPlan;
        }
    });

    const entityDto = bymoPageContextPropsResolveBusinessEntity(
        bymoPageContextProps,
        {businessEntity: businessEntityType, id: entityId}
    );

    if (!entityDto || doppeBusinessEntityDtoIsNotFound(entityDto)) {
        return '';
    }

    switch (entityType) {
        case DoppeLinkSpecEntityType.Product: {
            const product = entityDto as DoppeDtoStoreProduct;
            return product.slug ?? '';
        }
        case DoppeLinkSpecEntityType.Service: {
            const service = entityDto as DoppeDtoBookingService;
            return service.slug ?? '';
        }
        case DoppeLinkSpecEntityType.BlogPost: {
            const blogPost = entityDto as DoppeDtoBlogPost;
            return blogPost.slug ?? '';
        }
        case DoppeLinkSpecEntityType.Event: {
            const event = entityDto as DoppeDtoEvent;
            return event.slug ?? '';
        }
        default:
            // Unknown entity type - return empty string (entityId is not a valid slug)
            return '';
    }
}

/**
 * Entity info resolved from business entities data.
 * Contains slug, page URL, and inner type for navigation.
 */
export interface BymoPageResolvedEntityInfo {
    slug: string;
    pageUrl?: string | null;
    /**
     * The inner type of the entity for specific page routing:
     * - Product: 'physical' | 'digital' (from productType)
     * - Service: 'APPOINTMENT' | 'CLASS' | 'COURSE' (from type)
     * - Event: 'Rsvp' | 'Ticketing' (from registrationType)
     * When undefined, use default navigation (physical product, appointment, rsvp event).
     */
    innerType?: string;
}

/**
 * Resolves entity info (slug and page URL) from business entities data.
 * Used for navigation that needs to check useHoppVerticalItemPages flag.
 */
export function bymoPageContextPropsResolveEntityInfo(
    bymoPageContextProps: BymoPageContextProps,
    entityType: DoppeLinkSpecEntityType,
    entityId: string
): BymoPageResolvedEntityInfo {
    const businessEntityType = evaluateFunction(() => {
        switch (entityType) {
            case DoppeLinkSpecEntityType.Product:
                return DoppeBusinessEntity.HoppProduct;
            case DoppeLinkSpecEntityType.Service:
                return DoppeBusinessEntity.HoppService;
            case DoppeLinkSpecEntityType.BlogPost:
                return DoppeBusinessEntity.HoppBlogPost;
            case DoppeLinkSpecEntityType.Event:
                return DoppeBusinessEntity.HoppEvent;
            case DoppeLinkSpecEntityType.PricingPlan:
                return DoppeBusinessEntity.HoppPricingPlan;
        }
    });

    const entityDto = bymoPageContextPropsResolveBusinessEntity(
        bymoPageContextProps,
        {businessEntity: businessEntityType, id: entityId}
    );

    if (!entityDto || doppeBusinessEntityDtoIsNotFound(entityDto)) {
        // Return empty slug when entity not found - entityId is not a valid slug
        return {slug: '', pageUrl: undefined};
    }

    switch (entityType) {
        case DoppeLinkSpecEntityType.Product: {
            const product = entityDto as DoppeDtoStoreProduct;
            return {
                slug: product.slug ?? '',
                pageUrl: product.productPageUrl,
                innerType: product.productType // 'physical' | 'digital' | 'unspecified_product_type'
            };
        }
        case DoppeLinkSpecEntityType.Service: {
            const service = entityDto as DoppeDtoBookingService;
            return {
                slug: service.slug ?? '',
                pageUrl: service.servicePageUrl,
                innerType: service.type // 'APPOINTMENT' | 'CLASS' | 'COURSE'
            };
        }
        case DoppeLinkSpecEntityType.BlogPost: {
            const blogPost = entityDto as DoppeDtoBlogPost;
            return {
                slug: blogPost.slug ?? '',
                pageUrl: blogPost.blogPostPageUrl
                // No inner type for blog posts
            };
        }
        case DoppeLinkSpecEntityType.Event: {
            const event = entityDto as DoppeDtoEvent;
            return {
                slug: event.slug ?? '',
                pageUrl: event.eventPageUrl,
                innerType: event.registrationType // 'Rsvp' | 'Ticketing'
            };
        }
        case DoppeLinkSpecEntityType.PricingPlan: {
            const plan = entityDto as DoppeDtoMembershipPlan;
            return {
                slug: plan.id ?? '',
                pageUrl: plan.membershipPageUrl,
            };
        }
    }
}

